import {
  TOASTS,
  TOAST_TYPES,
  TOAST_CLASS,
  DEFAULT_OPTIONS,
} from '@/composables/toast/consts'
import type { ToastType, ToastOptions } from '@/composables/toast/consts'

import { useEmitter } from '@/composables/emitter'

import { EVENTS } from '@/lib/consts.ts'

import Toastify from 'toastify-js'
import 'toastify-js/src/toastify.css'
import { useLogger } from '@booksy-com/vue-tools'
import DOMPurify from 'dompurify'

type ToastInstance = {
  showToast: () => void
  hideToast: () => void
}

type DebugOptions = {
  debugMessage?: Error | string
  [key: string]: any // TODO: refine type
}

const getClassName = (options?: ToastOptions): string =>
  [options?.className, TOAST_CLASS].filter(Boolean).join(' ')

const showToast = (type: ToastType, text: string, options?: ToastOptions): void => {
  text = transformText(text, options)
  ;(Toastify({
    ...TOASTS[type],
    ...options,
    className: getClassName(options),
    text,
  }) as ToastInstance)
    .showToast()
}

const error = (text: string, options?: ToastOptions): void => {
  const emitter = useEmitter()
  emitter.emit(EVENTS.TOAST_ERROR, text)

  return showToast(TOAST_TYPES.ERROR, text, options)
}

const success = (text: string, options?: ToastOptions): void => {
  return showToast(TOAST_TYPES.SUCCESS, text, options)
}

const info = (text: string, options?: ToastOptions): void => {
  return showToast(TOAST_TYPES.INFO, text, options)
}

const warn = (text: string, options?: ToastOptions): void => {
  return showToast(TOAST_TYPES.WARN, text, options)
}

const show = (text: string, options?: ToastOptions): void => {
  return (Toastify({
    ...DEFAULT_OPTIONS,
    ...options,
    className: getClassName(options),
    text,
  }) as ToastInstance).showToast()
}

const goAway = (toast: ToastInstance): void => {
  toast.hideToast()
}

const getDebugMessage = (text: string, options: DebugOptions = {}): string | null => {
  try {
    if (options.debugMessage instanceof Error) {
      // api error handling
      if ((options.debugMessage as any).response) {
        const response = (options.debugMessage as any).response

        if (response?.data.errors?.[0]?.description) {
          return [
            response?.data.errors?.[0]?.field,
            response?.data.errors?.[0]?.code,
            response?.data.errors?.[0]?.description,
          ].filter((v: any) => !!v).join(' | ')
        }

        if (response?.data.error) {
          return response?.data.error
        }

        if (response?.data.message) {
          return response?.data.message
        }
      }

      // js standard error
      return options.debugMessage.message
    }

    return options.debugMessage as string | null
  } catch (e) {
    const logger = useLogger('toast')
    logger.error(e)
    return null
  }
}

const transformText = (text: string, options?: ToastOptions): string => {
  const DEBUG_MESSAGE_LENGTH = 100

  let debugMessage = getDebugMessage(text, options)

  if (!debugMessage || debugMessage === text) {
    return (DOMPurify.sanitize(text, { RETURN_DOM: true }) as HTMLElement).innerHTML
  }

  debugMessage = debugMessage.length > DEBUG_MESSAGE_LENGTH
    ? debugMessage.substring(0, DEBUG_MESSAGE_LENGTH) + '...'
    : debugMessage

  // nosemgrep: html-in-template-string
  return `${(DOMPurify.sanitize(text, { RETURN_DOM: true }) as HTMLElement).innerHTML} <div class="toastedDebugMessage">${debugMessage}</div>`
}

type UseToastReturn = {
  success: (text: string, options?: ToastOptions) => void
  error: (text: string, options?: ToastOptions) => void
  info: (text: string, options?: ToastOptions) => void
  show: (text: string, options?: ToastOptions) => void
  goAway: (toast: ToastInstance) => void
  warn: (text: string, options?: ToastOptions) => void
}

export const useToast = (): UseToastReturn => {
  return {
    success,
    error,
    info,
    show,
    goAway,
    warn,
  }
}
